/**
 * 슬라이드 design tokens 직접 편집 — palette / fonts / typeScale / radius.
 * master-of-slide 의 style-panel 미러.
 *
 * ThemeGallery 가 사전 정의 7종을 한 클릭으로 적용한다면, 여기서는 그 토큰을
 * 슬라이더/색상 picker 로 미세 조정. 변경은 디바운스 500ms 후 자동 저장.
 */
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { api } from '@/lib/api';
import type { SlideDesign } from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface Props {
  slideId: string;
  design: SlideDesign;
}

const FONT_PRESETS: Array<{ label: string; value: string }> = [
  { label: 'Pretendard', value: 'Pretendard, system-ui, sans-serif' },
  { label: 'Noto Sans KR', value: '"Noto Sans KR", system-ui, sans-serif' },
  { label: 'Noto Serif KR', value: '"Noto Serif KR", Georgia, serif' },
  { label: 'Inter', value: 'Inter, system-ui, sans-serif' },
  { label: 'JetBrains Mono', value: '"JetBrains Mono", "D2Coding", ui-monospace, monospace' },
  { label: '시스템 sans', value: 'system-ui, -apple-system, sans-serif' },
  { label: '시스템 serif', value: 'Georgia, "Times New Roman", serif' },
];

export function DesignPanel({ slideId, design }: Props) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<SlideDesign>(design);
  // 외부에서 design 갱신 (모델이 set_design 호출 등) 시 동기화.
  useEffect(() => {
    setDraft(design);
  }, [design]);

  const setDesignMut = useMutation({
    mutationFn: (next: SlideDesign) => api.slideSetDesign(slideId, { design: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['slide', slideId] }),
  });

  // 디바운스 저장 — 슬라이더/색상 picker 의 빠른 변경을 묶어 한 번만 PATCH.
  useDebouncedCallback(draft, 500, (next) => {
    if (
      next.palette.bg !== design.palette.bg ||
      next.palette.text !== design.palette.text ||
      next.palette.accent !== design.palette.accent ||
      next.fonts.display !== design.fonts.display ||
      next.fonts.body !== design.fonts.body ||
      next.typeScale.hero !== design.typeScale.hero ||
      next.typeScale.body !== design.typeScale.body ||
      next.radius !== design.radius
    ) {
      setDesignMut.mutate(next);
    }
  });

  const updatePalette = (key: keyof SlideDesign['palette'], value: string) => {
    setDraft((d) => ({ ...d, palette: { ...d.palette, [key]: value } }));
  };
  const updateFont = (key: keyof SlideDesign['fonts'], value: string) => {
    setDraft((d) => ({ ...d, fonts: { ...d.fonts, [key]: value } }));
  };
  const updateTypeScale = (key: keyof SlideDesign['typeScale'], value: number) => {
    setDraft((d) => ({ ...d, typeScale: { ...d.typeScale, [key]: value } }));
  };

  return (
    <div className="space-y-3 text-xs">
      <div className="text-[11px] uppercase tracking-wide text-foreground-muted flex items-center justify-between">
        <span>직접 조정</span>
        <span className="text-[10px]">
          {setDesignMut.isPending ? '저장 중…' : setDesignMut.isError ? '저장 실패' : '자동 저장됨'}
        </span>
      </div>

      <Section title="팔레트">
        <ColorRow label="배경" value={draft.palette.bg} onChange={(v) => updatePalette('bg', v)} />
        <ColorRow
          label="본문"
          value={draft.palette.text}
          onChange={(v) => updatePalette('text', v)}
        />
        <ColorRow
          label="강조"
          value={draft.palette.accent}
          onChange={(v) => updatePalette('accent', v)}
        />
      </Section>

      <Section title="폰트">
        <FontRow
          label="제목"
          value={draft.fonts.display}
          onChange={(v) => updateFont('display', v)}
        />
        <FontRow label="본문" value={draft.fonts.body} onChange={(v) => updateFont('body', v)} />
      </Section>

      <Section title="크기">
        <SliderRow
          label={`Hero (${draft.typeScale.hero}px)`}
          min={80}
          max={240}
          step={4}
          value={draft.typeScale.hero}
          onChange={(v) => updateTypeScale('hero', v)}
        />
        <SliderRow
          label={`Body (${draft.typeScale.body}px)`}
          min={20}
          max={60}
          step={2}
          value={draft.typeScale.body}
          onChange={(v) => updateTypeScale('body', v)}
        />
        <SliderRow
          label={`모서리 (${draft.radius}px)`}
          min={0}
          max={32}
          step={1}
          value={draft.radius}
          onChange={(v) => setDraft((d) => ({ ...d, radius: v }))}
        />
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] text-foreground-muted">{title}</div>
      {children}
    </div>
  );
}

function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  // <input type="color"> 는 #rrggbb 만 받음. 기존 value 가 그 형식이 아니면 native fallback 표시.
  const hex = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000';
  return (
    <label className="flex items-center gap-2">
      <span className="text-foreground-muted w-10">{label}</span>
      <input
        type="color"
        value={hex}
        onChange={(e) => onChange(e.target.value)}
        className="size-7 rounded border border-border bg-transparent cursor-pointer"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 font-mono text-[11px] rounded-md border border-border bg-surface-muted/30 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </label>
  );
}

function FontRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  // 값이 preset 과 일치하면 select 그 값, 아니면 'custom' 보여주고 textarea 같이.
  const matched = FONT_PRESETS.find((p) => p.value === value);
  return (
    <div className="space-y-1">
      <label className="flex items-center gap-2">
        <span className="text-foreground-muted w-10">{label}</span>
        <select
          value={matched?.value ?? '__custom__'}
          onChange={(e) => {
            if (e.target.value !== '__custom__') onChange(e.target.value);
          }}
          className="flex-1 text-[11px] rounded-md border border-border bg-surface-muted/30 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-accent"
        >
          {FONT_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
          {!matched && <option value="__custom__">커스텀</option>}
        </select>
      </label>
      {!matched && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full ml-12 font-mono text-[10px] rounded-md border border-border bg-surface-muted/30 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-accent"
        />
      )}
    </div>
  );
}

function SliderRow({
  label,
  min,
  max,
  step,
  value,
  onChange,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <span className="text-foreground-muted text-[10px] w-28 shrink-0">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number.parseInt(e.target.value, 10))}
        className="flex-1 accent-accent"
      />
    </label>
  );
}
